import { apiService } from "./apiService";

/**
 * Get site-based analytics data
 * @param {string|number} tenantId - Tenant ID
 * @returns {Promise<Object>} Analytics data with totalSpent, totalSites, topSites, allSites
 */
export const getSiteAnalytics = async (tenantId) => {
  const params = new URLSearchParams();
  if (tenantId) {
    params.append("tenant_id", tenantId);
  }

  const queryString = params.toString();
  const url = queryString
    ? `/analytics/sites?${queryString}`
    : "/analytics/sites";

  return apiService.request(url);
};

/**
 * Get trend analytics data
 * @param {string|number} tenantId - Tenant ID
 * @param {number} months - Number of months to look back (default: 12)
 * @returns {Promise<Object>} Trend analytics data
 */
export const getTrendAnalytics = async (tenantId, months = 12) => {
  const params = new URLSearchParams();
  if (tenantId) {
    params.append("tenant_id", tenantId);
  }
  params.append("months", months);

  const queryString = params.toString();
  const url = `/analytics/trends?${queryString}`;

  return apiService.request(url);
};

/**
 * Get monthly report analytics for a specific month
 * @param {string|number} tenantId - Tenant ID
 * @param {number} year - Year (e.g., 2024)
 * @param {number} month - Month (1-12)
 * @returns {Promise<Object>} Monthly report data
 */
export const getMonthlyReport = async (tenantId, year, month) => {
  const params = new URLSearchParams();
  if (tenantId) {
    params.append("tenant_id", tenantId);
  }
  if (year) {
    params.append("year", year);
  }
  if (month) {
    params.append("month", month);
  }

  const queryString = params.toString();
  const url = `/snapshots/report?${queryString}`;

  return apiService.request(url);
};

/**
 * Get invoice-based report for a month (totals and provider from monthly_invoices by invoice_date)
 */
export const getInvoiceReport = async (tenantId, year, month) => {
  const params = new URLSearchParams();
  if (tenantId) params.append("tenant_id", tenantId);
  if (year) params.append("year", year);
  if (month) params.append("month", month);
  return apiService.request(`/analytics/invoice-report?${params.toString()}`);
};

/**
 * Get count-based Site Footprint report for a fiscal year period.
 */
/** Closed Site Financials — snapshot blob KPIs + rows */
// export const getClosedSiteFinancials = async (tenantId, year, month) => {
//   const params = new URLSearchParams();
//   if (tenantId) params.append("tenant_id", tenantId);
//   if (year != null) params.append("year", year);
//   if (month != null) params.append("month", month);
//   return apiService.request(`/analytics/closed-site-financials?${params.toString()}`);
// };

export const getClosedSiteFinancials = async (tenantId, year, month) => {
  const now = new Date();

  const params = new URLSearchParams();
  if (tenantId) params.append("tenant_id", tenantId);
  params.append("year", year ?? now.getFullYear());
  params.append("month", month ?? now.getMonth() + 1);

  return apiService.request(`/analytics/closed-site-financials?${params.toString()}`);
};

/**
 * Circuit-level MRC history from monthly snapshots for a full calendar year.
 * Supports server-side pagination, search, filters, and sort.
 */
export const getMrcHistoryReport = async (tenantId, year, options = {}) => {
  const params = new URLSearchParams();
  if (tenantId) params.append("tenant_id", tenantId);
  if (year) params.append("year", year);
  if (options.page != null) params.append("page", String(options.page));
  if (options.limit != null) params.append("limit", String(options.limit));
  if (options.search) params.append("search", options.search);
  if (options.changeFilter && options.changeFilter !== "all") {
    params.append("changeFilter", options.changeFilter);
  }
  if (options.provider && options.provider !== "all") {
    params.append("provider", options.provider);
  }
  if (options.category && options.category !== "all") {
    params.append("category", options.category);
  }
  if (options.sortKey) params.append("sortKey", options.sortKey);
  if (options.sortDir) params.append("sortDir", options.sortDir);
  if (options.refresh) params.append("refresh", "true");
  return apiService.request(`/analytics/mrc-history-report?${params.toString()}`);
};

// export const getSiteFootprintReport = async (tenantId, year, month) => {
//   const params = new URLSearchParams();
//   if (tenantId) params.append("tenant_id", tenantId);
//   if (year) params.append("year", year);
//   if (month) params.append("month", month);
//   return apiService.request(`/analytics/site-footprint-report?${params.toString()}`);
// };

export const getSiteFootprintReport = async (tenantId) => {
  const params = new URLSearchParams();
  if (tenantId) params.append("tenant_id", tenantId);

  return apiService.request(
    `/analytics/site-footprint-report?${params.toString()}`
  );
};

/**
 * Get monthly spending trend from invoices (sum by invoice_date month)
 */
export const getInvoiceTrends = async (tenantId, months = 12) => {
  const params = new URLSearchParams();
  if (tenantId) params.append("tenant_id", tenantId);
  params.append("months", months);
  return apiService.request(`/analytics/invoice-trends?${params.toString()}`);
};

/**
 * Tenant Overview dashboard aggregates.
 * GET /api/analytics/overview
 */
export const getOverview = async () => apiService.request("/analytics/overview");

/**
 * Year-to-date portfolio report (invoice-based monthly spend vs budget).
 * GET /api/reports/ytd?year=&month=&annual_budget=&gl_code=
 * @param {string} [glCode] - optional GL filter; omit or "" for all
 */
export const getYtdReport = async (year, month = null, annualBudget, glCode = "") => {
  const params = new URLSearchParams();
  params.append("year", String(year));
  params.append("month", String(month));
  params.append("annual_budget", String(annualBudget));
  if (glCode != null && String(glCode).trim()) {
    params.append("gl_code", String(glCode).trim());
  }
  return apiService.request(`/reports/ytd?${params.toString()}`);
};

/**
 * Enqueue / regenerate month invoice note summary for a closed month.
 * POST /api/analytics/month-summaries/:year/:month/generate
 */
export const generateMonthSummary = async (year, month) =>
  apiService.request(`/analytics/month-summaries/${year}/${month}/generate`, {
    method: "POST",
    body: JSON.stringify({}),
  });

/**
 * Manually edit / save month invoice note summary text.
 * PUT /api/analytics/month-summaries/:year/:month
 */
export const updateMonthSummary = async (year, month, summaryText) =>
  apiService.request(`/analytics/month-summaries/${year}/${month}`, {
    method: "PUT",
    body: JSON.stringify({ summary_text: summaryText }),
  });

/**
 * Get report settings (e.g. annual budget).
 * Super Admin: pass tenantId to scope the tenant.
 */
export const getReportSettings = async (tenantId) => {
  const params = new URLSearchParams();
  if (tenantId != null && tenantId !== "") params.set("tenant_id", String(tenantId));
  const qs = params.toString();
  return apiService.request(`/analytics/report-settings${qs ? `?${qs}` : ""}`);
};

/**
 * Update report settings. Body may include annualBudgetByYear and/or footprintBaselineByYear (site footprint baseline, not $ budget).
 * Super Admin: include tenantId (sent as tenant_id).
 */
export const updateReportSettings = async (payload, tenantId) => {
  const body = { ...(payload || {}) };
  if (tenantId != null && tenantId !== "") body.tenant_id = tenantId;
  return apiService.request("/analytics/report-settings", {
    method: "PATCH",
    body: JSON.stringify(body),
  });
};

/**
 * Save budget for one calendar year (Super Admin → Tenant Configuration → Budget Entry).
 * Body: {
 *   year: number,
 *   granularity: "annual"|"monthly",
 *   entryType?: "provider"|"whole",
 *   lines: [...]
 * }
 * Super Admin: pass tenantId.
 */
export const saveProviderBudget = async (payload, tenantId) => {
  const body = { ...(payload || {}) };
  if (tenantId != null && tenantId !== "") body.tenant_id = tenantId;
  return apiService.request("/analytics/provider-budget", {
    method: "PUT",
    body: JSON.stringify(body),
  });
};
